import React, { useMemo } from 'react';
import { createAvatar } from '@dicebear/core';
import { 
  avataaars, 
  identicon, 
  initials, 
  lorelei, 
  personas, 
  thumbs,
  shapes,
  rings
} from '@dicebear/collection';
import { Box } from '@mui/material';

// Available avatar styles
const AVATAR_STYLES = {
  avataaars: avataaars,
  identicon: identicon,
  initials: initials,
  lorelei: lorelei,
  personas: personas,
  thumbs: thumbs,
  shapes: shapes,
  rings: rings
};

/**
 * UserAvatar - A reusable avatar component using DiceBear
 * 
 * @param {string} seed - Unique identifier (user ID, email, etc.) to generate consistent avatar
 * @param {number} size - Size of the avatar in pixels (default: 40)
 * @param {string} style - Avatar style from DiceBear collection (default: 'avataaars')
 * @param {string} className - Optional CSS class name
 * @param {object} sx - Material-UI sx prop for styling
 * @param {object} options - Additional DiceBear options (backgroundColor, etc.)
 */
const UserAvatar = ({ 
  seed, 
  size = 40, 
  style = 'avataaars', 
  className = '',
  sx = {},
  options = {},
  ...props 
}) => {
  // Generate avatar SVG using DiceBear
  const avatarSvg = useMemo(() => {
    if (!seed) {
      return null;
    }

    try {
        // console.log('AVAILABLE_AVATAR_STYLES:', AVAILABLE_AVATAR_STYLES);

      // Get the style collection, fallback to avataaars if style doesn't exist
      const avatarStyle = AVATAR_STYLES[style] || AVATAR_STYLES.avataaars;
      
      // Default options that work well for most styles
      const defaultOptions = {
        seed,
        size,
        // Common options that work across styles
        backgroundColor: ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf'],
        ...options
      };

      // Create avatar and get SVG string
      const avatar = createAvatar(avatarStyle, defaultOptions);
      return avatar.toString();
    } catch (error) {
      console.error('Error generating avatar:', error);
      return null;
    }
  }, [seed, size, style, options]);

  // If no SVG generated, return a fallback
  if (!avatarSvg) {
    return (
      <Box
        className={className}
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: '#e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: size * 0.4,
          color: '#9e9e9e',
          fontWeight: 'bold',
          ...sx
        }}
        {...props}
      >
        ?
      </Box>
    );
  }

  return (
    <Box
      className={className}
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        overflow: 'hidden',
        display: 'inline-block',
        ...sx
      }}
      dangerouslySetInnerHTML={{ __html: avatarSvg }}
      {...props}
    />
  );
};

export default UserAvatar;

// Export available styles for easy reference
export const AVAILABLE_AVATAR_STYLES = Object.keys(AVATAR_STYLES);
